// Translation lookup for React components — pairs with Rails helper
// `jsx_t(scope)` in app/helpers/application_helper.rb. The Rails side
// serializes a full subtree of config/locales/**/*.yml into a single
// `translations` prop; we walk it here by dotted path.
//
// Why no library: we already own a complete i18n catalogue server-side,
// and react-i18next would force us to duplicate / sync keys in JSON. This
// keeps a single source of truth in Rails YAML.
//
// Usage:
//   import { t } from '../i18n.js'
//   t('cancel', translations)
//   t('errors.invalid_token', translations)
//   t('count_label', translations, { count: 5 })
//
// Pluralization: pass { count } and the lookup will prefer `key.one` /
// `key.other` over the leaf — matches Rails I18n pluralization rules
// (English-only for now; add zero/few/many here if extending locales).

const INTERPOLATION_RE = /%\{(\w+)\}/g

// The React mount controller runs every prop through humps' camelizeKeys,
// which deep-camelizes the translations subtree too (`empty_state` ->
// `emptyState`, `tenant_name` -> `tenantName`, `GERMAN_HURDLE` ->
// `gERMANHURDLE`). Our keys are authored snake_case to mirror the Rails YAML,
// so when a literal segment is absent we fall back to its camelized form.
// This mirrors humps' exact algorithm so both camelized and raw trees resolve.
function camelize(str) {
  const c = str.replace(/[-_\s]+(.)?/g, (_, chr) => (chr ? chr.toUpperCase() : ''))
  return c.charAt(0).toLowerCase() + c.slice(1)
}

export function t(path, root, vars = {}) {
  if (!root) return path

  const parts = path.split('.')
  let node = root
  for (const part of parts) {
    if (node == null || typeof node !== 'object') return path
    node = part in node ? node[part] : node[camelize(part)]
  }

  // Pluralization branch: leaf is an object with one/other and caller
  // passed a numeric `count` — pick the right form.
  if (node && typeof node === 'object' && vars && typeof vars.count === 'number') {
    const form = vars.count === 1 ? 'one' : 'other'
    if (typeof node[form] === 'string') node = node[form]
    else if (typeof node.other === 'string') node = node.other
  }

  if (typeof node !== 'string') return path

  return node.replace(INTERPOLATION_RE, (_, key) =>
    vars[key] != null ? String(vars[key]) : `%{${key}}`
  )
}
